import { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useIngredients } from '@/hooks/useIngredients';
import { useInventory } from '@/hooks/useInventory';
import { useTranslation } from 'react-i18next';
import { IngredientIcon } from '@/components/ui/ingredient-icon';
import { Input } from '@/components/ui/input';
import { LayoutGrid, Search, XCircle, AlertTriangle, CheckCircle2 } from 'lucide-react';

interface InventoryOverviewChartProps {
  restaurantId: string;
  onIngredientClick?: (ingredientId: string) => void;
}

type StockStatus = 'inStock' | 'lowStock' | 'outOfStock' | 'noInventory';

interface IngredientItem {
  ingredientId: string;
  name: string;
  nameAr: string;
  nameFr: string;
  emoji: string;
  quantity: number;
  unit: string;
  minThreshold: number;
  status: StockStatus;
}

const STATUS_ORDER: Record<StockStatus, number> = {
  outOfStock: 0,
  lowStock: 1,
  noInventory: 2,
  inStock: 3,
};

const STATUS_STYLES: Record<StockStatus, string> = {
  outOfStock: 'border-red-400 bg-red-50 dark:bg-red-950/30 dark:border-red-800',
  lowStock: 'border-yellow-400 bg-yellow-50 dark:bg-yellow-950/30 dark:border-yellow-800',
  noInventory: 'border-slate-300 bg-slate-50 dark:bg-slate-900/30 dark:border-slate-700',
  inStock: 'border-green-300 bg-green-50/50 dark:bg-green-950/20 dark:border-green-900',
};

export function InventoryOverviewChart({ restaurantId, onIngredientClick }: InventoryOverviewChartProps) {
  const { t, i18n } = useTranslation();
  const { data: ingredients } = useIngredients(restaurantId);
  const { data: inventory } = useInventory(restaurantId);
  const [search, setSearch] = useState('');

  const allItems = useMemo<IngredientItem[]>(() => {
    if (!ingredients || ingredients.length === 0) return [];

    return ingredients.map((ingredient) => {
      const inv = inventory?.find((i) => i.ingredientId === ingredient.id);
      const name = i18n.language === 'ar' ? ingredient.nameAr : ingredient.nameFr;

      let status: StockStatus = 'noInventory';
      if (inv) {
        if (inv.quantity <= 0) {
          status = 'outOfStock';
        } else if (inv.quantity <= inv.minThreshold) {
          status = 'lowStock';
        } else {
          status = 'inStock';
        }
      }

      return {
        ingredientId: ingredient.id,
        name,
        nameAr: ingredient.nameAr,
        nameFr: ingredient.nameFr,
        emoji: ingredient.emoji || '',
        quantity: inv?.quantity ?? 0,
        unit: inv ? t(`inventory.units.${inv.unit}`) : '',
        minThreshold: inv?.minThreshold ?? 0,
        status,
      };
    });
  }, [ingredients, inventory, i18n.language, t]);

  const filteredItems = useMemo(() => {
    const sorted = [...allItems].sort(
      (a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status]
    );

    if (!search.trim()) return sorted;

    const q = search.toLowerCase().trim();
    return sorted.filter(
      (item) =>
        item.nameAr.toLowerCase().includes(q) ||
        item.nameFr.toLowerCase().includes(q)
    );
  }, [allItems, search]);

  if (!ingredients || ingredients.length === 0) {
    return null;
  }

  const outOfStockCount = allItems.filter((d) => d.status === 'outOfStock').length;
  const lowStockCount = allItems.filter((d) => d.status === 'lowStock').length;
  const inStockCount = allItems.filter((d) => d.status === 'inStock').length;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg font-semibold flex items-center gap-2">
          <LayoutGrid className="h-5 w-5" />
          {t('inventory.insights.overviewChart')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Status summary pills */}
        <div className="flex flex-wrap gap-2">
          {outOfStockCount > 0 && (
            <div className="flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-full bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300">
              <XCircle className="w-3.5 h-3.5" />
              {t('inventory.insights.outOfStock')} ({outOfStockCount})
            </div>
          )}
          {lowStockCount > 0 && (
            <div className="flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300">
              <AlertTriangle className="w-3.5 h-3.5" />
              {t('inventory.lowStock')} ({lowStockCount})
            </div>
          )}
          <div className="flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-full bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300">
            <CheckCircle2 className="w-3.5 h-3.5" />
            {t('inventory.inStock')} ({inStockCount})
          </div>
        </div>

        {/* Search input */}
        <div className="relative">
          <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('inventory.insights.searchIngredients')}
            className="ps-9 h-9"
          />
        </div>

        {/* Ingredient grid */}
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2">
          {filteredItems.map((item) => (
            <button
              key={item.ingredientId}
              onClick={() => onIngredientClick?.(item.ingredientId)}
              className={`
                flex flex-col items-center gap-1 p-2 rounded-lg border-2 transition-all
                hover:scale-105 hover:shadow-md active:scale-95
                ${STATUS_STYLES[item.status]}
              `}
            >
              <IngredientIcon
                ingredient={{ nameAr: item.nameAr, nameFr: item.nameFr, emoji: item.emoji }}
                size={32}
                className="w-8 h-8"
              />
              <span className="text-[10px] sm:text-xs font-medium leading-tight text-center line-clamp-2 w-full">
                {item.name}
              </span>
              {item.status !== 'noInventory' && (
                <span className="text-[9px] sm:text-[10px] text-muted-foreground leading-tight">
                  {item.quantity.toFixed(1)} {item.unit}
                </span>
              )}
            </button>
          ))}
        </div>

        {filteredItems.length === 0 && search.trim() && (
          <p className="text-center text-sm text-muted-foreground py-4">
            {t('inventory.insights.noData')}
          </p>
        )}

        {/* Tap hint */}
        <p className="text-center text-xs text-muted-foreground">
          {t('inventory.insights.tapToScroll')}
        </p>
      </CardContent>
    </Card>
  );
}
